/**
 * `Promise.all` over a mapped list starts every request in the same tick. That
 * is fine for a handful of items, but the ingredient screens fan out one
 * third-party lookup per recent ingredient — 40+ concurrent requests — which
 * saturates a weak connection and turns a slow network into a wall of
 * timeouts. This keeps at most `limit` promises in flight while preserving the
 * order of the results.
 *
 * Rejections propagate like `Promise.all`: the first one wins. Nothing new is
 * pulled off the queue after that — work already in flight still runs to
 * completion, since a started promise cannot be cancelled.
 */
export const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length)
  let nextIndex = 0
  let failed = false

  const runNext = async (): Promise<void> => {
    const index = nextIndex
    nextIndex += 1

    if (failed || index >= items.length) {
      return
    }

    try {
      results[index] = await worker(items[index], index)
    } catch (error) {
      failed = true

      throw error
    }

    return runNext()
  }

  const workerCount = Math.max(1, Math.min(limit, items.length))

  await Promise.all(Array.from({ length: workerCount }, runNext))

  return results
}
